import { getClient } from '@/api/api-client';
import type { RentingEvents } from '@condo/domain';
import {
    type ICommonTableFetchParams,
    type IEstate,
    type IInvestment,
    type IRentingDetails,
    type IRentingEstate,
    type IRentingLead,
    type IRentingListing,
    InvestmentCase,
    RentingLead,
    RentingListing,
    RentingProcess,
} from '@condo/domain';
import type {
    FindRentalContractsQueryParams,
    FindRentalContractsQueryResponse,
    FindRentingLeadsQueryParams,
    GetRentingListingQueryParams,
    UpdateRentalContractsEstarCoreIdMutationRequest,
    UpsertRentalContractMutationRequest,
    UpsertRentalContractMutationResponse,
} from '@condo/sdk/propx';

export const findRentingEstates = async (params?: ICommonTableFetchParams): Promise<{ estates: IRentingEstate[]; total: number }> =>
    getClient('basecamp')
        .get('/renting/estates', { params })
        .then(response => {
            const { estates, total } = response.data;
            const estatesWithRentingProcess = estates.map((estate: IEstate) => ({
                ...estate,
                renting: estate.renting ? new RentingProcess({ ...estate.renting, events: estate.calendarEvents }) : undefined,
                executedInvestmentCase: estate.executedInvestmentCase
                    ? new InvestmentCase({ ...(estate.executedInvestmentCase as IInvestment), estate })
                    : undefined,
            }));
            return { estates: estatesWithRentingProcess, total };
        });

/*Renting*/
export const getRentingDetailsOfEstate = async (estate: IEstate): Promise<IRentingDetails[]> => {
    const { rentingDetails } = await getClient('propx').getRentingDetailsOfEstate({ estateId: estate.estateId });
    return rentingDetails.map(rd => {
        return {
            ...rd,
            executedInvestmentCase: rd.executedInvestmentCase ? new InvestmentCase({ ...(rd.executedInvestmentCase as IInvestment), estate }) : undefined,
        };
    }) as IRentingDetails[];
};

export const findRentingLeads = (params: FindRentingLeadsQueryParams): Promise<RentingLead[]> =>
    getClient('propx')
        .findRentingLeads(params)
        .then(data => data.rentingLeads.map(r => new RentingLead(r as IRentingLead)));

export const removeRentingLead = (rentingLeadId: number) => getClient('propx').deleteRentingLeadById({ rentingLeadId });

export const fetchEstateRentalContracts = async (estateId: number): Promise<FindRentalContractsQueryResponse['rentalContracts']> => {
    const { rentalContracts } = await fetchRentalContracts({ query: { estateId } });
    return rentalContracts;
};

export const fetchRentalContracts = async (params: FindRentalContractsQueryParams): Promise<FindRentalContractsQueryResponse> =>
    getClient('propx').findRentalContracts(params);

/**
 * @deprecated moved to RAM
 */
export const createOrUpdateRentalContract = async (data: UpsertRentalContractMutationRequest): Promise<UpsertRentalContractMutationResponse> =>
    getClient('propx').upsertRentalContract(data);

export const updateRentalContractsEstarCoreId = async (data: UpdateRentalContractsEstarCoreIdMutationRequest): Promise<void> => {
    await getClient('propx').updateRentalContractsEstarCoreId(data);
};

export const getRentingListing = async (params: GetRentingListingQueryParams): Promise<RentingListing | null> =>
    getClient('propx')
        .getRentingListing(params)
        .then(data => (data ? new RentingListing(data as IRentingListing) : null));

export const postRentingProcessEvent = async (processId: number, event: RentingEvents | string, additionalData?: Record<string, unknown>): Promise<unknown> =>
    getClient('basecamp').post(`/renting-process/${processId}/event`, { event, additionalData: additionalData ?? {} });
